import {
  BlotterDensity,
  BlotterTable,
  type DateOnly,
  useBlotterTable,
  type useMultiMarketDataSnapshot,
} from '@talos/kyoko';
import type { Fixing } from './fixingSlice';
import { fixingInputID, useFixingBlotterColumns } from './useFixingBlotterColumns';
import { type FixingBlotterRow, useFixingBlotterDataObs } from './useFixingBlotterDataObs';

interface FixingBlotterProps {
  fixings: Fixing[];
  onFixingChange: (fixingIndex: string, fixingPrice: string) => void;
  multiMarketDataObs: ReturnType<typeof useMultiMarketDataSnapshot>;
  fixingDate: DateOnly | undefined;
}

export const FixingBlotter = ({ fixings, multiMarketDataObs, onFixingChange, fixingDate }: FixingBlotterProps) => {
  const dataObservable = useFixingBlotterDataObs(fixings, multiMarketDataObs, fixingDate);
  const columns = useFixingBlotterColumns({ onFixingChange });

  const blotterTable = useBlotterTable<FixingBlotterRow>({
    dataObservable,
    errorObservable: undefined,
    rowID: 'rowID' satisfies keyof FixingBlotterRow,
    density: BlotterDensity.Comfortable,
    applyTransactionsSynchronously: true,
    gridOptions: {
      suppressHeaderFocus: true,
      focusGridInnerElement: params => {
        // Called when focus is going into the blotter
        // Can either be "from the top" or "from the bottom" (tab vs shift+tab)
        // We resolve the correct input element and manually call focus on it on behalf of aggrid
        let rowCount = 0;
        params.api.forEachNode(() => rowCount++);
        const rowIndexToFocus = params.fromBottom ? rowCount - 1 : 0;
        const inputElement = document.getElementById(fixingInputID(rowIndexToFocus)) as HTMLInputElement | null;
        if (inputElement) {
          inputElement.focus();
          inputElement.select(); // selects all text, needs to be after focus
        }

        return true; // tells aggrid that we took care of the focusing ourselves
      },
    },
    columns,
  });

  return <BlotterTable {...blotterTable} />;
};
